import { useState } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Target, Users, Calendar, Clock, CheckCircle } from 'lucide-react';

function KPIsPage() {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  // Mock KPI data
  const kpiData = {
    month: {
      responseTime: { value: 4.2, change: -0.8, trend: 'down' },
      completionRate: { value: 94.5, change: 2.1, trend: 'up' },
      userSatisfaction: { value: 8.7, change: 0.3, trend: 'up' },
      efficiency: { value: 87.3, change: -1.2, trend: 'down' }
    },
    quarter: {
      responseTime: { value: 4.8, change: -1.2, trend: 'down' },
      completionRate: { value: 92.1, change: 3.4, trend: 'up' },
      userSatisfaction: { value: 8.4, change: 0.8, trend: 'up' },
      efficiency: { value: 85.9, change: 2.1, trend: 'up' }
    },
    year: {
      responseTime: { value: 5.1, change: -2.3, trend: 'down' },
      completionRate: { value: 89.7, change: 5.2, trend: 'up' },
      userSatisfaction: { value: 8.1, change: 1.2, trend: 'up' },
      efficiency: { value: 83.2, change: 4.8, trend: 'up' }
    }
  };

  const currentData = kpiData[selectedPeriod];

  const getTrendIcon = (trend) => {
    if (trend === 'up') {
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    }
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-green-600' : 'text-red-600';
  };

  const getChangeText = (change) => {
    return change > 0 ? `+${change}` : change.toString();
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Key Performance Indicators</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Monitor and analyze performance metrics across the organization
          </p>
        </div>
        
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Response Time */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex items-center space-x-1">
              {getTrendIcon(currentData.responseTime.trend)}
              <span className={`text-sm font-medium ${getTrendColor(currentData.responseTime.trend)}`}>
                {getChangeText(currentData.responseTime.change)}
              </span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Response Time</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{currentData.responseTime.value}h</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Average response time</p>
        </div>

        {/* Completion Rate */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex items-center space-x-1">
              {getTrendIcon(currentData.completionRate.trend)}
              <span className={`text-sm font-medium ${getTrendColor(currentData.completionRate.trend)}`}>
                {getChangeText(currentData.completionRate.change)}%
              </span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Completion Rate</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{currentData.completionRate.value}%</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Tasks completed successfully</p>
        </div>

        {/* User Satisfaction */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex items-center space-x-1">
              {getTrendIcon(currentData.userSatisfaction.trend)}
              <span className={`text-sm font-medium ${getTrendColor(currentData.userSatisfaction.trend)}`}>
                {getChangeText(currentData.userSatisfaction.change)}
              </span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">User Satisfaction</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{currentData.userSatisfaction.value}/10</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Average satisfaction score</p>
        </div>

        {/* Efficiency */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
              <Target className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="flex items-center space-x-1">
              {getTrendIcon(currentData.efficiency.trend)}
              <span className={`text-sm font-medium ${getTrendColor(currentData.efficiency.trend)}`}>
                {getChangeText(currentData.efficiency.change)}%
              </span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Efficiency</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{currentData.efficiency.value}%</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Overall operational efficiency</p>
        </div>
      </div>

      {/* Performance Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performance Trends</h3>
          <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <BarChart3 className="mx-auto h-12 w-12 mb-3" />
              <p>Chart visualization would go here</p>
              <p className="text-sm">Showing trends over {selectedPeriod}</p>
            </div>
          </div>
        </div>

        {/* Comparison Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Period Comparison</h3>
          <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <BarChart3 className="mx-auto h-12 w-12 mb-3" />
              <p>Comparison chart would go here</p>
              <p className="text-sm">Month vs Quarter vs Year</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Detailed Metrics</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Current
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Previous
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Change
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Trend
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {Object.entries(currentData).map(([key, data]) => (
                <tr key={key}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {typeof data.value === 'number' && data.value % 1 !== 0 ? data.value.toFixed(1) : data.value}
                    {key === 'userSatisfaction' ? '/10' : key === 'completionRate' || key === 'efficiency' ? '%' : key === 'responseTime' ? 'h' : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {typeof data.value === 'number' && data.value % 1 !== 0 ? (data.value - data.change).toFixed(1) : (data.value - data.change)}
                    {key === 'userSatisfaction' ? '/10' : key === 'completionRate' || key === 'efficiency' ? '%' : key === 'responseTime' ? 'h' : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <span className={`${getTrendColor(data.trend)}`}>
                      {getChangeText(data.change)}
                      {key === 'completionRate' || key === 'efficiency' ? '%' : key === 'responseTime' ? 'h' : ''}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getTrendIcon(data.trend)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default KPIsPage;
